"use client";

import { Button } from "rizzui";
import cn from "@core/utils/class-names";
import { useScrollableSlider } from "@core/hooks/use-scrollable-slider";
import {
  PiBank,
  PiCaretLeftBold,
  PiCaretRightBold,
  PiCube,
  PiCurrencyCircleDollar,
  PiFolder,
} from "react-icons/pi";
import TransactionCard, {
  TransactionType,
} from "@core/components/cards/transaction-card";
import { Payments, Withdrawal } from "packages/app/src/types";
import { useEffect, useState } from "react";
import { sumByFieldGrouped } from "../../../../utils/sum-fields";
import { formatAmount } from "../../../../utils/format-amount";

type FileStatsType = {
  className?: string;
  payments: Payments[];
  withdrawals: Withdrawal[];
};

export function StatGrid({
  payments,
  withdrawals,
}: {
  payments: Payments[];
  withdrawals: Withdrawal[];
}) {
  const [totalDeposite, setTotalDeposite] = useState({
    NGN: 0,
    USDT: 0,
  });
  const [totalWithdrawals, setTotalWithdrawals] = useState<number>(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState<number>(0);

  const statData: TransactionType[] = [
    {
      title: "Total Deposite (NGN)",
      amount: formatAmount(totalDeposite.NGN, "NGN"),
      increased: true,
      percentage: "32.45",
      icon: PiBank,
      iconWrapperFill: "#8A63D2",
    },
    {
      title: "Total Deposite (USDT)",
      amount: formatAmount(totalDeposite.USDT, "USDT"),
      increased: true,
      percentage: "32.45",
      icon: PiBank,
      iconWrapperFill: "#63d2a4",
    },
    {
      title: "Total Withdrawal",
      amount: formatAmount(totalWithdrawals, "NGN"),
      increased: false,
      percentage: "32.45",
      icon: PiCube,
      iconWrapperFill: "#00CEC9",
    },
    {
      title: "Pending Withdrawal",
      amount: formatAmount(pendingWithdrawals, "NGN"),
      increased: true,
      percentage: "32.45",
      icon: PiCurrencyCircleDollar,
      iconWrapperFill: "#0070F3",
    },
  ];

  useEffect(() => {
    /* GETTING ALL DEPOSITE */
    const donations = sumByFieldGrouped(
      payments,
      "totalAmount",
      "currencyType",
      "success"
    );
    // console.log(donations);
    setTotalDeposite({
      NGN: donations.NGN,
      USDT: donations.USDT,
    });
    /* GETTING ALL WITHDRAWALS */
    const withdrawalTotal = sumByFieldGrouped(withdrawals, "amount", undefined);
    setTotalWithdrawals(withdrawalTotal.all);
    /* GETTING ALL PENDING WITHDRAWALS */
    const pendingWithdrawalTotal = sumByFieldGrouped(
      withdrawals,
      "amount",
      undefined,
      "pending"
    );
    setPendingWithdrawals(pendingWithdrawalTotal.all);
  }, [payments, withdrawals]);
  return (
    <>
      {statData.map((stat: any, index: number) => {
        return (
          <TransactionCard
            key={"transaction-card-" + index}
            transaction={stat}
            className="min-w-[300px]"
          />
        );
      })}
    </>
  );
}

export default function FileStats({
  className,
  payments,
  withdrawals,
}: FileStatsType) {
  const {
    sliderEl,
    sliderPrevBtn,
    sliderNextBtn,
    scrollToTheRight,
    scrollToTheLeft,
  } = useScrollableSlider();

  return (
    <div
      className={cn(
        "relative flex w-auto items-center overflow-hidden",
        className
      )}
    >
      <Button
        title="Prev"
        variant="text"
        ref={sliderPrevBtn}
        onClick={() => scrollToTheLeft()}
        className="!absolute -left-1 top-0 z-10 !h-full w-20 !justify-start rounded-none bg-gradient-to-r from-gray-0 via-gray-0/70 to-transparent px-0 ps-1 text-gray-500 hover:text-gray-900 dark:from-gray-50 dark:via-gray-50/70 3xl:hidden"
      >
        <PiCaretLeftBold className="h-5 w-5" />
      </Button>
      <div className="w-full overflow-hidden">
        <div
          ref={sliderEl}
          className="custom-scrollbar-x grid grid-flow-col gap-5 overflow-x-auto scroll-smooth 2xl:gap-6 3xl:gap-8 [&::-webkit-scrollbar]:h-0"
        >
          <StatGrid payments={payments} withdrawals={withdrawals} />
        </div>
      </div>
      <Button
        title="Next"
        variant="text"
        ref={sliderNextBtn}
        onClick={() => scrollToTheRight()}
        className="dark: !absolute -right-2 top-0 z-10 !h-full w-20 !justify-end rounded-none bg-gradient-to-l from-gray-0 via-gray-0/70 to-transparent px-0 pe-2 text-gray-500 hover:text-gray-900 dark:from-gray-50 dark:via-gray-50/70 3xl:hidden"
      >
        <PiCaretRightBold className="h-5 w-5" />
      </Button>
    </div>
  );
}
